import { useEffect, useRef, useState } from "react"
import { Box, CloseButton, Dialog, HStack, Portal, Separator, Spacer, VStack } from "@chakra-ui/react"
import { ALL_STEPS, getClosingMeetingDescription, SCHEDULE_CLOSING_MEETING_STEP_INDEX } from "@/constants"
import { Deal } from "@/types"
import { Button, StepItem, Text } from "../ui"

interface Props {
  deals: Deal[]
  currentDeal: Deal | null
  onDealChange: (deal: Deal) => void
  isOpen: boolean
  onClose: () => void
}

export const WhatsNextModal = ({ deals, currentDeal, onDealChange, isOpen, onClose }: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const activeStepRef = useRef<HTMLDivElement>(null)
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  useEffect(() => {
    if (isOpen && activeStepRef.current && scrollContainerRef.current) {
      // slight timeout so modal fully renders before scrolling
      setTimeout(() => {
        activeStepRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 100)
    }
  }, [isOpen])

  useEffect(() => {
    if (currentDeal) {
      const index = ALL_STEPS.findIndex(step => step.step === currentDeal.currentStep);
      setCurrentStepIndex(index !== -1 ? index : 0);
    }
  }, [currentDeal]);

  return (
    <Dialog.Root size="full" open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <VStack pt={10} px={{ base: 4, md: 10 }} pb={5} gap={5} flex="1" overflow="hidden" align="stretch">
              <Text fontSize="2xl" fontWeight="bold">Here's What's Next</Text>
              <HStack mt={5} gap={2}>
                {
                  deals && deals.length === 1 ? (
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600" borderWidth={1} borderColor="gray.200" borderRadius="md" px={4} py={2}>
                      {deals[0].company.name}
                    </Text>
                  ) : (
                    deals && deals.map((deal, index) => (
                      <Button
                        key={index}
                        variant={ deal.id === currentDeal?.id ? "solid" : "outline"}
                        onClick={() => onDealChange(deal)}
                      >
                        {deal.company.name}
                      </Button>
                    ))
                  )
                }
              </HStack>
              <Separator />
              <VStack ref={scrollContainerRef} overflowY="scroll" maxH="70vh">
                {
                  ALL_STEPS.map((step, index) => {
                    const ref = step.isActive ? activeStepRef : undefined
                    const displayStep = index === SCHEDULE_CLOSING_MEETING_STEP_INDEX
                      ? { ...step, description: getClosingMeetingDescription(currentDeal?.dealManager?.calendly) }
                      : step
                    return (
                      <Box key={index} ref={ref} w="100%">
                        <StepItem
                          step={displayStep}
                          isCurrent={index === currentStepIndex}
                          isCompleted={index < (currentStepIndex ?? 0)}
                          isLast={index === ALL_STEPS.length - 1}
                          isClosed={currentStepIndex === ALL_STEPS.length - 1}
                        />
                      </Box>
                    )
                  })
                }
              </VStack>
              <Spacer />
              <HStack justify="flex-end">
                <Button variant="outline" onClick={onClose}>Done</Button>
              </HStack>
            </VStack>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}